import type { AuditVertical } from '../types/audit';

/**
 * Vertical-aware label set for the audit report UI. The backend fills the
 * same card VALUES for both verticals; only the human-readable LABELS differ
 * (e-commerce reads "Customer" where mobile-app reads "Subscriber").
 */
export interface ReportLabels {
  /** KPI label for total revenue ever generated. "Lifetime Revenue" reads as
   *  subscription/customer-LTV outside subscription/e-commerce — B2B uses
   *  "Total Revenue" instead (the underlying number is won-deal revenue,
   *  not a per-customer lifetime figure). */
  lifetimeRevenueLabel: string;
  /** KPI label for the active-audience count. */
  activeCount: string;
  /** Card title for the audience-health section. */
  audienceHealth: string;
  /** Chart legend for the active-audience trend line. */
  audienceTrendLegend: string;
  /** Chart legend for the "new" trend line. */
  newLegend: string;
  /** Conversion-funnel top stage. */
  funnelTop: string;
  /** Conversion-funnel middle stage. */
  funnelMid: string;
  /** Conversion-funnel bottom stage. */
  funnelBottom: string;
  /** KPI label for the churnRate slot (subscription churn vs lapsed-buyer rate). */
  repeatMetric: string;
  /** KPI label for the renewalRate slot (subscription renewal vs repeat-purchase rate). */
  renewalMetric: string;
  /** Labels for the 3 cohort-retention cards (retentionM1/M3/M12 slots) —
   *  month-cohort subscription retention vs day-windowed repeat-purchase %. */
  retentionM1Label: string;
  retentionM3Label: string;
  retentionM12Label: string;
  /** KPI label for the arpu slot — Average Revenue Per USER (MRR ÷ active
   *  subscribers) has no e-commerce equivalent (no per-user recurring
   *  revenue); the slot is repurposed for AOV there instead. */
  arpuLabel: string;
}

const MOBILE_APP_LABELS: ReportLabels = {
  lifetimeRevenueLabel: 'Lifetime Revenue',
  activeCount: 'Active Subscribers',
  audienceHealth: 'Subscriber Health',
  audienceTrendLegend: 'Active Subscribers',
  newLegend: 'New Paid',
  funnelTop: 'Installs',
  funnelMid: 'Trial Starts',
  funnelBottom: 'Paid Subscriber Conversions',
  repeatMetric: 'Monthly Churn',
  renewalMetric: 'Renewal Rate',
  retentionM1Label: 'M1 Retention',
  retentionM3Label: 'M3 Retention',
  retentionM12Label: 'M12 Retention',
  arpuLabel: 'ARPU',
};

// Repeat@30/60/90d are DAY-windowed cohort percentages (retentionM1/M3/M12
// slots repurposed) — not the month-granularity M1/M3/M12 subscription
// retention. See repeat_cohort_days in compute_ecommerce_kpis.py.
const ECOMMERCE_LABELS: ReportLabels = {
  lifetimeRevenueLabel: 'Lifetime Revenue',
  activeCount: 'Active Buyers',
  audienceHealth: 'Customer Health',
  audienceTrendLegend: 'Active Buyers',
  newLegend: 'New Customers',
  funnelTop: 'Sessions',
  funnelMid: 'Reached Checkout',
  funnelBottom: 'Purchases',
  repeatMetric: 'Lapsed Customer Rate',
  renewalMetric: 'Repeat Purchase Rate',
  retentionM1Label: 'Repeat @30d',
  retentionM3Label: 'Repeat @60d',
  retentionM12Label: 'Repeat @90d',
  arpuLabel: 'AOV',
};

// Neutral wording for a hybrid business (Shopify + subscriptions both
// present) or one with no connected revenue source yet — "Subscribers" or
// "Customers" alone would misrepresent it either way. See
// `_project_vertical` (src/api/routes/audit.py) for how this is decided.
const GENERIC_LABELS: ReportLabels = {
  lifetimeRevenueLabel: 'Lifetime Revenue',
  activeCount: 'Active Buyers',
  audienceHealth: 'Customer Health',
  audienceTrendLegend: 'Active Buyers',
  newLegend: 'New Customers',
  funnelTop: 'Visitors',
  funnelMid: 'Engaged',
  funnelBottom: 'Conversions',
  repeatMetric: 'Lapsed Customer Rate',
  renewalMetric: 'Repeat Purchase Rate',
  retentionM1Label: 'Repeat @30d',
  retentionM3Label: 'Repeat @60d',
  retentionM12Label: 'Repeat @90d',
  arpuLabel: 'AOV',
};

// "Qualified @7d/30d/90d" repurposes the 3 retention card slots as lead
// velocity: % of leads that reached Qualified status within N days of
// creation — B2B has no subscription-style cohort retention, but lead→
// qualified speed is the closest analogous "is the pipeline healthy" signal.
// Placeholder pending real B2B data — revisit label wording once B2B audits
// have run through the system.
const B2B_LABELS: ReportLabels = {
  lifetimeRevenueLabel: 'Total Revenue',
  activeCount: 'Leads',
  audienceHealth: 'Pipeline Health',
  audienceTrendLegend: 'Leads',
  newLegend: 'New Leads',
  funnelTop: 'Visitors',
  funnelMid: 'Qualified Leads',
  funnelBottom: 'Won Deals',
  repeatMetric: 'Lost-Customer Rate',
  renewalMetric: 'Renewal / Expansion Rate',
  retentionM1Label: 'Qualified @7d',
  retentionM3Label: 'Qualified @30d',
  retentionM12Label: 'Qualified @90d',
  arpuLabel: 'Avg Deal Size',
};

// Default stays 'mobile_app' so reports persisted before `_vertical` existed
// (the key is entirely absent, not just unset) keep rendering unchanged.
export const labelsFor = (v: AuditVertical = 'mobile_app'): ReportLabels => {
  if (v === 'ecommerce') return ECOMMERCE_LABELS;
  if (v === 'generic') return GENERIC_LABELS;
  if (v === 'b2b') return B2B_LABELS;
  return MOBILE_APP_LABELS;
};
